import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Search, Filter, X, SlidersHorizontal } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { SearchFilterProps, FilterOptions } from "@/types";

interface ExtendedSearchFilterProps extends SearchFilterProps {
  onFilter?: (filters: FilterOptions) => void;
  showDomainFilter?: boolean;
  showStatusFilter?: boolean;
  showSortOptions?: boolean;
}

export const SearchFilter = ({
  onSearch,
  onFilterChange,
  placeholder,
  showDomainFilter = true,
  showStatusFilter = false,
  showSortOptions = true,
}: ExtendedSearchFilterProps) => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState<FilterOptions>({});
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const domains = [
    { value: "all", label: t('common.all') },
    { value: "strategic-thinking", label: t('domains.strategicThinking') },
    { value: "executing", label: t('domains.executing') },
    { value: "influencing", label: t('domains.influencing') },
    { value: "relationship-building", label: t('domains.relationshipBuilding') },
  ];

  const statusOptions = [
    { value: "all", label: t('common.all') },
    { value: "completed", label: t('search.completed') },
    { value: "in-progress", label: t('search.inProgress') },
    { value: "not-started", label: t('search.notStarted') },
  ];

  const sortOptions = [
    { value: "name", label: t('search.name') },
    { value: "date", label: t('search.date') },
    { value: "progress", label: t('search.progress') },
    { value: "rank", label: t('search.rank') },
  ];

  const handleSearch = (value: string) => {
    setSearchQuery(value);
    onSearch(value);
  };

  const handleFilterChange = (key: keyof FilterOptions, value: string) => {
    const newFilters = { ...filters, [key]: value === 'all' ? undefined : value };
    setFilters(newFilters);
    onFilterChange?.(newFilters);
  };

  const clearFilters = () => {
    setFilters({});
    setSearchQuery("");
    onSearch("");
    onFilterChange?.({});
  };

  const activeFilterCount = Object.values(filters).filter(v => v !== undefined).length;

  return (
    <div className="space-y-4 text-start" dir={isRtl ? "rtl" : "ltr"}>
      {/* Search Bar */}
      <div className="flex flex-col gap-2 sm:flex-row">
        <div className="relative min-w-0 flex-1">
          <Search className="absolute start-3 top-1/2 w-4 h-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder={placeholder || t('search.searchPlaceholder')}
            className="ps-10 bg-background/50"
          />
          {searchQuery && (
            <button
              onClick={() => handleSearch("")}
              className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Filter Button */}
        <Popover open={isFilterOpen} onOpenChange={setIsFilterOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="relative w-full sm:w-auto"
            >
              <SlidersHorizontal className="h-4 w-4" />
              {t('common.filter')}
              {activeFilterCount > 0 && (
                <Badge
                  variant="destructive"
                  className="ms-2 flex h-5 w-5 items-center justify-center rounded-full p-0 text-xs"
                >
                  {activeFilterCount}
                </Badge>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[calc(100vw-2rem)] text-start sm:w-80" align={isRtl ? "start" : "end"} dir={isRtl ? "rtl" : "ltr"}>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-foreground">{t('search.filters')}</h4>
                {activeFilterCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    className="h-auto p-1 text-xs"
                  >
                    {t('search.clearAll')}
                  </Button>
                )}
              </div>

              {/* Domain Filter */}
              {showDomainFilter && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">
                    {t('search.domain')}
                  </label>
                  <Select
                    value={filters.domain || "all"}
                    onValueChange={(value) => handleFilterChange('domain', value)}
                  >
                    <SelectTrigger className="bg-background/50 text-start">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent dir={isRtl ? "rtl" : "ltr"}>
                      {domains.map((domain) => (
                        <SelectItem key={domain.value} value={domain.value}>
                          {domain.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Status Filter */}
              {showStatusFilter && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">
                    {t('search.status')}
                  </label>
                  <Select
                    value={filters.status || "all"}
                    onValueChange={(value) => handleFilterChange('status', value)}
                  >
                    <SelectTrigger className="bg-background/50 text-start">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent dir={isRtl ? "rtl" : "ltr"}>
                      {statusOptions.map((status) => (
                        <SelectItem key={status.value} value={status.value}>
                          {status.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Sort Options */}
              {showSortOptions && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">
                    {t('search.sortBy')}
                  </label>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <Select
                      value={filters.sortBy || "name"}
                      onValueChange={(value) => handleFilterChange('sortBy', value)}
                    >
                      <SelectTrigger className="flex-1 bg-background/50 text-start">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent dir={isRtl ? "rtl" : "ltr"}>
                        {sortOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={filters.sortOrder || "asc"}
                      onValueChange={(value) => handleFilterChange('sortOrder', value as 'asc' | 'desc')}
                    >
                      <SelectTrigger className="w-full bg-background/50 text-start sm:w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent dir={isRtl ? "rtl" : "ltr"}>
                        <SelectItem value="asc">{t('search.ascending')}</SelectItem>
                        <SelectItem value="desc">{t('search.descending')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              <Button
                onClick={() => setIsFilterOpen(false)}
                className="w-full bg-primary hover:bg-primary/90"
              >
                {t('search.applyFilters')}
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* Active Filters Display */}
      {(searchQuery || activeFilterCount > 0) && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-muted-foreground">{t('search.activeFilters')}:</span>
          
          {searchQuery && (
            <Badge variant="secondary" className="gap-1">
              {t('search.searchTerm')}: "{searchQuery}"
              <button onClick={() => handleSearch("")} className="ms-1">
                <X className="w-3 h-3" />
              </button>
            </Badge>
          )}
          
          {filters.domain && (
            <Badge variant="secondary" className="gap-1">
              {t('search.domain')}: {domains.find(d => d.value === filters.domain)?.label}
              <button onClick={() => handleFilterChange('domain', 'all')} className="ms-1">
                <X className="w-3 h-3" />
              </button>
            </Badge>
          )}
          
          {filters.status && (
            <Badge variant="secondary" className="gap-1">
              {t('search.status')}: {statusOptions.find(s => s.value === filters.status)?.label}
              <button onClick={() => handleFilterChange('status', 'all')} className="ms-1">
                <X className="w-3 h-3" />
              </button>
            </Badge>
          )}
          
          {activeFilterCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="h-6 text-xs"
            >
              {t('search.clearAll')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
